import { getLiDARDataVisualizationSummariesForLastSpecificHeats } from "../services/getLiDARDataVisualizationSummariesForLastSpecificHeats";
import { LidarDataSummaryForVisualizationMapped } from "../types/getLiDARDataVisualizationSummariesForLastSpecificHeats";
import ScatterWorker, { ScatterWorkerTypes } from "../workers/ScatterWorker";
import WorkerFactory from "~/workers/workerFactory";
import {
  VISUAL_MAP_BOTTOM_COLORS_SCALE,
  VISUAL_MAP_SIDEWALL_COLORS_SCALE,
} from "../../../constants/heatMapValues";
import { useCallback, useEffect, useState } from "react";
import { GET_LIDAR_DATA_VISUALIZATION_SUMMARIES_FOR_LAST_SPECIFIC_HEATS } from "~/constants/swrKey";
import { notification } from "antd";
import { LidarZoneSelectionModel } from "../../laserScanScene/types/laserScanScene";

interface VisualizationSummaries {
  sidewallVisulizationSummaries: LidarDataSummaryForVisualizationMapped[];
  bottomVisualizationSummaries: LidarDataSummaryForVisualizationMapped[];
  lastAggregatedData: LidarZoneSelectionModel[];
}

const fetcher = async (campaignHeatId: string) => {
  const workerInstance = WorkerFactory(ScatterWorker);

  try {
    const result = await getLiDARDataVisualizationSummariesForLastSpecificHeats(
      {
        campaignHeatId,
        specificHeatCount: 5, //TODO: Will be changed after determine performance
      },
    );

    return new Promise<VisualizationSummaries>((resolve, reject) => {
      if (result?.length > 0) {
        workerInstance.postMessage({
          type: ScatterWorkerTypes.GET_PROCESSED_LIDAR_DATA,
          payload: {
            data: result ? result : [],
            bottomColorScale: VISUAL_MAP_BOTTOM_COLORS_SCALE,
            sidewallColorScale: VISUAL_MAP_SIDEWALL_COLORS_SCALE,
            lastHeatId: campaignHeatId,
          },
        });

        workerInstance.onmessage = (
          e: MessageEvent<{
            type: ScatterWorkerTypes;
            payload: VisualizationSummaries;
          }>,
        ) => {
          const { type, payload } = e.data;

          if (type === ScatterWorkerTypes.GET_PROCESSED_LIDAR_DATA) {
            resolve(payload);
            workerInstance.terminate();
          }
        };
      } else {
        reject(undefined);
        workerInstance.terminate();
      }
    });
  } catch (_) {
    workerInstance.terminate();
  }
};

export const useGetLiDARDataVisualizationSummariesForLastSpecificHeats = (
  campaignHeatId: string,
) => {
  const [{ data, loading }, setData] = useState<{
    data?: VisualizationSummaries;
    loading: boolean;
  }>({
    data: undefined,
    loading: true,
  });

  const swrKey = createSWRKey(campaignHeatId);

  const runFetcher = useCallback(async () => {
    try {
      const data = await fetcher(campaignHeatId);
      setData({ data, loading: false });
    } catch (e) {
      notification.error({
        message: "Error",
        description: "Failed to load conversations",
      });

      setData({ loading: false });
    }
  }, [campaignHeatId]);

  useEffect(() => {
    runFetcher();
  }, [swrKey, runFetcher]);

  if (!data) {
    return {
      sidewallVisulizationSummaries: [],
      bottomVisualizationSummaries: [],
      lastAggregatedData: [],
      visualizationSummariesLoading: loading,
    };
  }

  return {
    sidewallVisulizationSummaries: data.sidewallVisulizationSummaries,
    bottomVisualizationSummaries: data.bottomVisualizationSummaries,
    lastAggregatedData: data.lastAggregatedData,
    visualizationSummariesLoading: loading || data === undefined,
  };
};

const createSWRKey = (campaignHeatId: string) => {
  return [
    GET_LIDAR_DATA_VISUALIZATION_SUMMARIES_FOR_LAST_SPECIFIC_HEATS,
    campaignHeatId,
  ].join("|");
};
